import type { TFunction } from 'i18next'
import type { ElementType } from 'react'
import {
  LifebuoyIcon, MapPinIcon, UserGroupIcon, BuildingOffice2Icon,
  ClipboardDocumentListIcon, AcademicCapIcon, CalendarIcon, HeartIcon,
  GlobeAmericasIcon, PhotoIcon, BeakerIcon, ChartBarIcon, CpuChipIcon,
  Squares2X2Icon, PresentationChartLineIcon, BellIcon, TrophyIcon, BookOpenIcon,
  Cog6ToothIcon, LockClosedIcon, ArrowDownTrayIcon,
} from '@heroicons/react/24/outline'

/**
 * MODELLO DI NAVIGAZIONE DICHIARATIVO — sorgente UNICA per desktop e mobile.
 *
 * Prima la navigazione di ruolo era duplicata e divergente: `Sidebar` (desktop)
 * era volutamente minimale e delegava alla dashboard, mentre `BottomNav` (mobile)
 * aveva i propri set per ruolo. Qui le voci "primarie" di ciascun ruolo sono
 * definite una volta sola e consumate da entrambe le superfici → stesse voci,
 * stesse destinazioni, ovunque. (UX-01 §4)
 *
 * NB: il gating lato client è solo un suggerimento all'interfaccia; il permesso
 * reale vive nei servizi.
 */

export interface NavCtx {
  profileType: string | null
  isAdmin: boolean
  isSales: boolean
  hasCapability: (cap: string) => boolean
  isResearchCenter: boolean
  isPhotographer: boolean
  isCharterOp: boolean
}

export interface NavEntry {
  to: string
  label: string
  icon: ElementType
  /** Prefissi di rotta extra per cui la voce risulta attiva. */
  match?: string[]
}

/** Hub "Diving": Logbook + Siti + Pianificazione in un'unica voce. */
const divingTab = (t: TFunction): NavEntry => ({
  to: '/app/diving',
  label: t('dash.diving'),
  icon: LifebuoyIcon,
  match: ['/app/diving', '/app/logbook', '/app/divesites', '/app/planning'],
})

/**
 * Voci PRIMARIE del ruolo attivo (il cuore della navigazione).
 * La cascata di risoluzione del ruolo è identica a quella storica del BottomNav,
 * così il comportamento resta fedele mentre la sorgente diventa unica.
 */
export function getPrimaryNav(ctx: NavCtx, t: TFunction): NavEntry[] {
  const { profileType, isAdmin, isResearchCenter, isPhotographer, isCharterOp, hasCapability } = ctx
  const isInstructor = hasCapability('INSTRUCTOR') || hasCapability('DIVEMASTER')

  if (isAdmin) return [
    { to: '/app/admin/hub',     label: t('nav.group.admin'),    icon: Squares2X2Icon,       match: ['/app/admin'] },
    { to: '/app/admin/users',   label: t('nav.userManagement'), icon: UserGroupIcon },
    { to: '/app/admin/centers', label: t('nav.divingCenters'),  icon: BuildingOffice2Icon },
    { to: '/app/analytics',     label: t('nav.analytics'),      icon: ChartBarIcon },
  ]

  if (profileType === 'DIVE_CENTER') return [
    { to: '/app/divecenter',          label: t('nav.dcPanel'),    icon: BuildingOffice2Icon },
    { to: '/app/divecenter/bookings', label: t('nav.dcBookings'), icon: ClipboardDocumentListIcon },
    { to: '/app/divecenter/courses',  label: t('nav.courses'),    icon: AcademicCapIcon },
    { to: '/app/divecenter/calendar', label: t('nav.dcCalendar'), icon: CalendarIcon },
  ]

  if (profileType === 'TRAVEL_AGENCY') return [
    { to: '/app/agency',          label: t('nav.agencyDashboard'), icon: BuildingOffice2Icon },
    { to: '/app/agency/packages', label: t('nav.travelPackages'),  icon: GlobeAmericasIcon },
    { to: '/app/agency/bookings', label: t('nav.bookings'),        icon: ClipboardDocumentListIcon },
    { to: '/app/tourism',         label: t('nav.tourism'),         icon: GlobeAmericasIcon },
  ]

  if (profileType === 'DIVE_DOCTOR') return [
    { to: '/app/dive-doctor',         label: t('nav.medicalTools'),  icon: HeartIcon },
    { to: '/app/doctor',              label: t('nav.medicalPortal'), icon: ClipboardDocumentListIcon },
    { to: '/app/medical/find-doctor', label: t('nav.findDoctor'),    icon: MapPinIcon },
    divingTab(t),
  ]

  if (isResearchCenter || profileType === 'ACADEMY') return [
    { to: '/app/academy',         label: t('nav.academyDashboard'),  icon: AcademicCapIcon },
    { to: '/app/research-center', label: t('nav.missionsDashboard'), icon: BeakerIcon },
    { to: '/app/courses',         label: t('nav.courses'),           icon: AcademicCapIcon },
    divingTab(t),
  ]

  if (isPhotographer) return [
    { to: '/app/photographer/portal',  label: t('nav.photoContribute'),    icon: PhotoIcon },
    { to: '/app/species/encyclopedia', label: t('nav.marineEncyclopedia'), icon: BeakerIcon },
    divingTab(t),
  ]

  if (profileType === 'ASSOCIATION') return [
    { to: '/app/associations',         label: t('nav.associationDashboard'), icon: UserGroupIcon },
    { to: '/app/associations/members', label: t('nav.members'),              icon: UserGroupIcon },
    { to: '/app/community',            label: t('nav.community'),            icon: UserGroupIcon },
    { to: '/app/chat',                 label: t('nav.chat'),                 icon: UserGroupIcon },
  ]

  if (profileType === 'PROFESSIONAL_DIVER') return [
    { to: '/app/professional', label: t('nav.professionalOperator', 'Operatore Prof.'), icon: ClipboardDocumentListIcon },
    divingTab(t),
    { to: '/app/community',    label: t('nav.community'), icon: UserGroupIcon },
  ]

  if (isCharterOp) return [
    { to: '/app/charter/my-charter', label: t('charter.nav.myCharter'), icon: GlobeAmericasIcon },
    { to: '/app/divesites',          label: t('nav.divesites'),         icon: MapPinIcon },
    { to: '/app/tourism',            label: t('nav.tourism'),           icon: GlobeAmericasIcon },
  ]

  if (isInstructor) return [
    { to: '/app/instructor', label: t('nav.instructor'), icon: AcademicCapIcon },
    { to: '/app/courses',    label: t('nav.courses'),    icon: AcademicCapIcon },
    divingTab(t),
  ]

  // DIVER / FREEDIVER (default)
  return [
    divingTab(t),
    { to: '/app/social',  label: t('nav.community'), icon: UserGroupIcon },
    { to: '/app/medical', label: t('nav.medical'),   icon: HeartIcon },
  ]
}

/** Voce trasversale: Assistente AI (tutti). */
export function getAiEntry(t: TFunction): NavEntry {
  return { to: '/app/ai', label: t('nav.ai'), icon: CpuChipIcon }
}

/** Voce separata CRM & Marketing (ruolo `sales`, già incluso admin). */
export function getCrmEntry(t: TFunction): NavEntry {
  return { to: '/app/crm-marketing', label: t('nav.group.crmMarketing', 'CRM & Marketing'), icon: PresentationChartLineIcon }
}

/** Voci di utilità (fondo sidebar). */
export function getUtilityNav(t: TFunction): NavEntry[] {
  return [
    { to: '/app/notifications', label: t('nav.notifications'),            icon: BellIcon },
    { to: '/app/achievements',  label: t('nav.achievements'),             icon: TrophyIcon },
    { to: '/app/manuali',       label: t('nav.manuals', 'Manuali'),       icon: BookOpenIcon },
    { to: '/app/settings',      label: t('nav.settings', 'Impostazioni'), icon: Cog6ToothIcon },
    { to: '/app/gdpr',          label: t('nav.privacy'),                  icon: LockClosedIcon },
    { to: '/app/install',       label: t('nav.install'),                  icon: ArrowDownTrayIcon },
  ]
}

/** Costruisce il contesto di navigazione dagli stati di auth (helper condiviso). */
export function buildNavCtx(opts: {
  profileType: string | null
  isAdmin: boolean
  isSales: boolean
  hasCapability: (cap: string) => boolean
  roles?: string[]
}): NavCtx {
  const roles = opts.roles ?? []
  return {
    profileType: opts.profileType,
    isAdmin: opts.isAdmin,
    isSales: opts.isSales,
    hasCapability: opts.hasCapability,
    isResearchCenter: roles.some(r => r === 'research_center') || opts.profileType === 'RESEARCH_CENTER',
    isPhotographer: roles.some(r => r.toLowerCase() === 'underwater_photographer') || opts.profileType === 'UNDERWATER_PHOTOGRAPHER',
    isCharterOp: roles.includes('CHARTER_OPERATOR'),
  }
}
